import { useEffect } from "react";
import {
  ensureTelegramWebAppScript,
  getTelegramLanguageCode,
  getTelegramPlatform,
  getTelegramStartParam,
  isTelegramMiniApp,
} from "./telegramAuth";
import { getSupabaseAccessToken, isSupabaseConfigured } from "./supabase";

/**
 * Product analytics — anonymous-by-default event beacon to the `track`
 * edge function. Same contract across all three apps in the monorepo
 * (Lexibit/GW2DO/Rememory): device-scoped queue in localStorage, flushed
 * on a timer and on tab hide.
 *
 * Design:
 *   - `device_id`: a client-generated uuid, persisted in localStorage —
 *     the only required identity. `user_id` is resolved server-side from
 *     the Supabase session (if any); the client sends a Bearer token when
 *     it has one, but never asserts a user id itself.
 *   - Consent: disabled until the user explicitly grants it. `track()` is a
 *     true no-op before consent, after denial, or when Supabase isn't
 *     configured (nothing to send to).
 *   - Queue: events are appended to a capped localStorage array, then
 *     flushed in a batch. A 10s timer covers the common case; `hidden`
 *     also flushes immediately (via sendBeacon, since the tab may be
 *     killed right after) so a session's tail isn't lost.
 */

// ---------------------------------------------------------------------------
// Event contract — keep in sync with supabase/functions/track/index.ts
// ---------------------------------------------------------------------------

export type AnalyticsEventName =
  | "daily_ping"
  | "tg_launch"
  | "onboarding_completed"
  | "drill_finished"
  | "study_finished"
  | "pack_installed"
  | "reference_dictionary_started"
  | "deck_created"
  | "word_added"
  | "ai_tutor_opened"
  | "ai_tutor_message_sent"
  | "account_created"
  | "sign_in"
  | "share_created"
  | "share_loop_created"
  | "share_sheet_opened"
  | "share_result"
  | "share_landed"
  | "share_preview_started"
  | "share_preview_completed"
  | "share_activated"
  | "backup_export"
  | "backup_import"
  | "data_reset"
  | "push_opt_in"
  | "push_opt_out";

type AnalyticsProps = Record<string, string | number | boolean | string[] | null | undefined>;

// ---------------------------------------------------------------------------
// Storage keys / constants
// ---------------------------------------------------------------------------

export const DEVICE_ID_KEY = "lexibit.analytics.deviceId";
export const OPT_OUT_KEY = "lexibit.analytics.optOut";
export const ANALYTICS_CONSENT_KEY = "lexibit.analytics.consent";
export const QUEUE_KEY = "lexibit.analytics.queue";
export const LAST_PING_DAY_KEY = "lexibit.analytics.lastPingDay";
// sessionStorage, not localStorage: a Telegram Mini App can reload its
// WebView mid-session (e.g. returning from the system browser), and a
// module-scope flag would reset on that reload and double-count tg_launch.
// sessionStorage survives a reload but not a fresh launch, which is exactly
// the "once per session" semantics we want.
export const TG_LAUNCH_SESSION_KEY = "lexibit.analytics.tgLaunchTracked";

const MAX_QUEUE = 200;
// Mirrors the server's per-request cap (supabase/functions/track/index.ts
// MAX_EVENTS) — flushing in slices of this size means a large backlog sends
// as several full batches instead of one oversized request the server would
// silently truncate.
const MAX_BATCH = 25;
const FLUSH_INTERVAL_MS = 10_000;
const CONSENT_CHANGE_EVENT = "lexibit:analytics-consent-change";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

type QueuedEvent = { name: AnalyticsEventName; props: AnalyticsProps; client_ts: string };

// ---------------------------------------------------------------------------
// Device id / opt-out
// ---------------------------------------------------------------------------

export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    // localStorage unavailable (e.g. private browsing) — a fresh id every
    // call means events won't dedupe by device, but tracking still no-ops
    // downstream since the queue write below will also fail silently.
    return crypto.randomUUID();
  }
}

/** Absence is intentionally denied: analytics is opt-in for every new profile. */
export function isAnalyticsOptedOut(): boolean {
  try {
    const consent = localStorage.getItem(ANALYTICS_CONSENT_KEY);
    if (consent === "granted") return false;
    if (consent === "denied") return true;
    // Preserve the only explicit preference written by older builds.
    if (localStorage.getItem(OPT_OUT_KEY) === "1") return true;
    return true;
  } catch {
    return true;
  }
}

export function setAnalyticsOptedOut(optedOut: boolean): void {
  try {
    if (optedOut) {
      localStorage.setItem(ANALYTICS_CONSENT_KEY, "denied");
      localStorage.setItem(OPT_OUT_KEY, "1");
      localStorage.removeItem(QUEUE_KEY);
      localStorage.removeItem(DEVICE_ID_KEY);
      localStorage.removeItem(LAST_PING_DAY_KEY);
      sessionStorage.removeItem(TG_LAUNCH_SESSION_KEY);
    } else {
      localStorage.setItem(ANALYTICS_CONSENT_KEY, "granted");
      localStorage.removeItem(OPT_OUT_KEY);
    }
  } catch {
    // ignore — same private-browsing fallback as above.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
  }
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

function readQueue(): QueuedEvent[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedEvent[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch {
    // ignore — worst case this batch of events is lost.
  }
}

function enqueue(event: QueuedEvent): void {
  const queue = readQueue();
  queue.push(event);
  // Cap at MAX_QUEUE, dropping the oldest — a long-offline device shouldn't
  // grow this unboundedly, and recent events are more useful than stale ones.
  while (queue.length > MAX_QUEUE) queue.shift();
  writeQueue(queue);
}

// ---------------------------------------------------------------------------
// track()
// ---------------------------------------------------------------------------

/**
 * Queue an analytics event for the next flush. No-op when Supabase isn't
 * configured or the user opted out — never throws, never blocks the caller.
 * Never send free-text content (word/message bodies) in `props`.
 */
export function track(name: AnalyticsEventName, props: AnalyticsProps = {}): void {
  if (!isSupabaseConfigured || isAnalyticsOptedOut()) return;
  enqueue({ name, props, client_ts: new Date().toISOString() });
}

// ---------------------------------------------------------------------------
// Flush
// ---------------------------------------------------------------------------

function trackUrl(): string {
  return `${SUPABASE_URL}/functions/v1/track`;
}

// sendBeacon can't set custom headers, so — for that path only — the anon
// apikey has to travel as a query param instead. The regular fetch path
// sends it as a header (see sendBatch()) and doesn't need this.
function trackUrlForBeacon(): string {
  return `${trackUrl()}?apikey=${encodeURIComponent(SUPABASE_ANON_KEY ?? "")}`;
}

function buildPayload(events: QueuedEvent[]): string {
  return JSON.stringify({
    device_id: getDeviceId(),
    events: events.map((e) => ({ name: e.name, props: e.props, client_ts: e.client_ts })),
  });
}

/** Sends one batch (already ≤ MAX_BATCH) via fetch. Resolves true only on a
    2xx response — a network error or a non-2xx (e.g. a 500) both count as
    failure, so the caller keeps the batch queued instead of losing it. */
async function sendBatch(batch: QueuedEvent[]): Promise<boolean> {
  try {
    const token = await getSupabaseAccessToken();
    const res = await fetch(trackUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY ?? "",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: buildPayload(batch),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Timer-driven flush: sends the queue in slices of {@link MAX_BATCH}. Each
    slice is removed from the queue only once it's actually been accepted by
    the server — a failed batch (offline, 500, ...) stays queued and is
    retried on the next flush rather than being lost. Exported for tests —
    production code only calls it from the interval in
    {@link useAnalyticsBoot}. */
export async function flush(): Promise<void> {
  if (!isSupabaseConfigured || isAnalyticsOptedOut()) return;
  let queue = readQueue();
  while (queue.length > 0) {
    const batch = queue.slice(0, MAX_BATCH);
    const ok = await sendBatch(batch);
    if (!ok) return; // leave this batch + the rest queued for next time
    queue = queue.slice(MAX_BATCH);
    writeQueue(queue);
  }
}

/** Tab-hide flush: sendBeacon (survives the page unloading), falling back to
    a keepalive fetch when unavailable. Sent in {@link MAX_BATCH} slices like
    {@link flush}; a slice is only dropped from the queue once sendBeacon
    reports it was actually handed off (its boolean return value) — if it
    returns false the remaining queue (including that slice) is left for the
    next flush. The keepalive-fetch fallback can't be awaited before the tab
    may close, so it stays best-effort/optimistic, same as before. Can't
    attach the Bearer token either way — sendBeacon doesn't support custom
    headers. Exported for tests. */
export function flushOnHide(): void {
  if (!isSupabaseConfigured || isAnalyticsOptedOut()) return;
  let queue = readQueue();
  const canBeacon = typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function";
  while (queue.length > 0) {
    const batch = queue.slice(0, MAX_BATCH);
    const payload = buildPayload(batch);
    if (canBeacon) {
      const sent = navigator.sendBeacon(trackUrlForBeacon(), new Blob([payload], { type: "application/json" }));
      if (!sent) return; // leave this batch + the rest queued for next time
    } else {
      fetch(trackUrl(), {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY ?? "" },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
    queue = queue.slice(MAX_BATCH);
    writeQueue(queue);
  }
}

// ---------------------------------------------------------------------------
// Automatic events: daily_ping (once/calendar day) + tg_launch (once/session)
// ---------------------------------------------------------------------------

function todayKey(): string {
  return new Date().toISOString().slice(0, 10); // YYYY-MM-DD, UTC calendar day
}

/** Exported for tests; called automatically once per mount by
    {@link useAnalyticsBoot}. */
export function pingDailyOnce(): void {
  if (isAnalyticsOptedOut()) return;
  try {
    if (localStorage.getItem(LAST_PING_DAY_KEY) === todayKey()) return;
    localStorage.setItem(LAST_PING_DAY_KEY, todayKey());
  } catch {
    // If we can't remember, better to skip than to spam on every reload.
    return;
  }
  track("daily_ping");
}

/** Exported for tests; called automatically once per mount by
    {@link useAnalyticsBoot}. */
export async function trackTelegramLaunchOnce(): Promise<void> {
  if (isAnalyticsOptedOut()) return;
  await ensureTelegramWebAppScript();
  // Consent can change while the optional host bridge is loading.
  if (isAnalyticsOptedOut()) return;
  if (!isTelegramMiniApp()) return;
  try {
    if (sessionStorage.getItem(TG_LAUNCH_SESSION_KEY) === "1") return;
    sessionStorage.setItem(TG_LAUNCH_SESSION_KEY, "1");
  } catch {
    // sessionStorage unavailable (e.g. private browsing) — track anyway;
    // a rare duplicate beats losing the event outright.
  }
  track("tg_launch", {
    platform: getTelegramPlatform(),
    language_code: getTelegramLanguageCode(),
    start_param: getTelegramStartParam(),
  });
}

/**
 * Wires the periodic flush timer + tab-hide flush, and fires the two
 * automatic events (`daily_ping`, `tg_launch`). Call once near the app
 * root (mirrors {@link useTelegramReadyAndExpand} in telegramChrome.ts).
 */
export function useAnalyticsBoot(): void {
  useEffect(() => {
    let stopActiveLifecycle = () => undefined;
    const applyConsent = () => {
      stopActiveLifecycle();
      stopActiveLifecycle = () => undefined;
      if (!isSupabaseConfigured || isAnalyticsOptedOut()) return;

      pingDailyOnce();
      void trackTelegramLaunchOnce();
      const timer = window.setInterval(() => {
        flush().catch(() => {});
      }, FLUSH_INTERVAL_MS);
      const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") flushOnHide();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      stopActiveLifecycle = () => {
        window.clearInterval(timer);
        document.removeEventListener("visibilitychange", onVisibilityChange);
      };
    };

    applyConsent();
    window.addEventListener(CONSENT_CHANGE_EVENT, applyConsent);
    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, applyConsent);
      stopActiveLifecycle();
    };
  }, []);
}
